import { Alert, Group, MultiSelect, Stack, Text, Textarea } from '@mantine/core';
import { AlertTriangle, UserMinus, UserPlus, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button, Card, Pill } from '../ui';
import { useAddCohortMembers, useRemoveCohortMember } from '../../hooks/useCohorts';
import { useUsers } from '../../hooks/useAdmin';
import type { CohortSummary } from '../../api/types';

/**
 * Who is in the cohort — the roster the gradebook groups by. Primary path is a
 * searchable picker of the tenant's existing users; a paste box (a column from
 * the registrar's sheet) remains as a fallback. Unknown addresses are reported,
 * not silently dropped.
 */
export function CohortMembers({ cohort }: { cohort: CohortSummary }) {
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const add = useAddCohortMembers(cohort.id);
  const remove = useRemoveCohortMember(cohort.id);
  const users = useUsers();
  const members = cohort.members ?? [];

  const memberEmails = useMemo(
    () => new Set(members.map((m) => m.username.toLowerCase())),
    [members],
  );
  // Only trainees can be enrolled — instructors and admins are staff.
  const options = useMemo(
    () =>
      (users.data ?? [])
        .filter((u) => u.role === 'trainee' && !memberEmails.has(u.email.toLowerCase()))
        .map((u) => ({ value: u.email.toLowerCase(), label: u.email })),
    [users.data, memberEmails],
  );

  const addPicked = async () => {
    if (!picked.length) return;
    await add.mutateAsync({ usernames: picked });
    setPicked([]);
  };

  const submitPasted = async () => {
    const usernames = text.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!usernames.length) return;
    await add.mutateAsync({ usernames });
    setText('');
  };

  return (
    <Stack gap="md">
      <Card>
        <Stack gap="sm">
          <Text fw={600} size="sm">Add students</Text>
          <MultiSelect
            data={options}
            value={picked}
            onChange={setPicked}
            placeholder={options.length ? 'Search users by email…' : 'No other users in this tenant yet'}
            searchable
            clearable
            nothingFoundMessage="No matching users"
            leftSection={<Users size={14} />}
            disabled={users.isPending || options.length === 0}
            aria-label="Select users to add to the cohort"
            maxDropdownHeight={260}
          />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Pick from users already in your tenant. Need someone new?{' '}
              Invite them from Admin → Users (Bulk invite accepts a CSV).
            </Text>
            <Button variant="primary" size="sm" leftIcon={<UserPlus size={12} />}
              onClick={() => void addPicked()} loading={add.isPending} disabled={!picked.length}>
              Add {picked.length || ''} to cohort
            </Button>
          </Group>

          <Text size="xs" c="dimmed" mt={4}>Or paste emails, one per line:</Text>
          <Textarea
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            placeholder={'alice@university.edu\nbob@university.edu'}
            autosize
            minRows={2}
            aria-label="Student emails or usernames, one per line"
          />
          <Group justify="flex-end">
            <Button variant="secondary" size="sm" leftIcon={<UserPlus size={12} />}
              onClick={() => void submitPasted()} loading={add.isPending} disabled={!text.trim()}>
              Add pasted
            </Button>
          </Group>

          {add.data ? (
            <Alert color={add.data.unknown.length || add.data.skipped?.length ? 'yellow' : 'green'} variant="light" icon={add.data.unknown.length || add.data.skipped?.length ? <AlertTriangle size={14} /> : undefined}>
              Added {add.data.added}.
              {add.data.unknown.length ? ` Not found: ${add.data.unknown.join(', ')}.` : ''}
              {add.data.skipped?.length ? ` Skipped (not trainees): ${add.data.skipped.join(', ')}.` : ''}
            </Alert>
          ) : null}
          {add.isError ? (
            <Alert color="red" variant="light" icon={<AlertTriangle size={14} />}>
              {(add.error as Error).message}
            </Alert>
          ) : null}
        </Stack>
      </Card>
      <Card>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={600} size="sm">Members</Text>
            <Pill variant={members.length ? 'info' : 'warn'} size="xs">
              {members.length}
            </Pill>
          </Group>
          {members.length === 0 ? (
            <Text size="xs" c="dimmed">No students yet.</Text>
          ) : (
            members.map((m) => (
              <Group key={m.user_id} justify="space-between" wrap="nowrap">
                <Text size="sm">{m.username}</Text>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<UserMinus size={12} />}
                  onClick={() => remove.mutate({ userId: m.user_id })}
                  loading={remove.isPending && remove.variables?.userId === m.user_id}
                >
                  Remove
                </Button>
              </Group>
            ))
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
